"use client";

import * as React from "react";
import { LayoutPanelLeft } from "lucide-react";
import { cn } from "~/lib/utils";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "~/components/ui/resizable";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "~/components/ui/sheet";

const HIRE_BUILDER_SPLIT_STORAGE_KEY = "siteglue-hire-builder-split";
const DEFAULT_LEFT_PANEL_PERCENT = 40;
const DEFAULT_RIGHT_PANEL_PERCENT = 60;

export type HireBuilderLeftTab = "build" | "configure";
export type HireBuilderRightTab =
  | "knowledge"
  | "preview"
  | "live_preview"
  | "report"
  | "company_settings"
  | "pricing"
  | "signup"
  | "hire"
  | "test_agent";

/** Action-only tab ids (do not change the content panel). */
export type HireBuilderActionTab = "contact_sales" | "book_demo";

type HireBuilderTabbedLayoutProps = {
  leftTab: HireBuilderLeftTab;
  onLeftTabChange: (tab: HireBuilderLeftTab) => void;
  rightTab: HireBuilderRightTab;
  onRightTabChange: (tab: HireBuilderRightTab) => void;
  buildPanel: React.ReactNode;
  configurePanel: React.ReactNode;
  previewPanel: React.ReactNode;
  /** SiteGlue live website + widget preview (available before Sign Up once generated). */
  livePreviewPanel?: React.ReactNode;
  /** SiteGlue Organization tab (knowledge + org fields). */
  knowledgePanel?: React.ReactNode;
  /** SiteGlue AI Implementation Strategy panel (optional). */
  reportPanel?: React.ReactNode;
  /** SiteGlue Settings (agent profile, colors, training, tools). */
  companySettingsPanel?: React.ReactNode;
  /** SiteGlue Pricing Plans panel (right-hand tab). */
  pricingPanel?: React.ReactNode;
  /** SiteGlue Sign Up panel (Clerk). */
  signupPanel?: React.ReactNode;
  hirePanel: React.ReactNode;
  /** Channel test panel (Web Chat, SMS, Voice, …). */
  testAgentPanel?: React.ReactNode;
  /** Agent first name for the Onboard tab label (e.g. "Onboard Tammy"). */
  onboardTabLabel?: string;
  /** Settings / agent profile tab label (e.g. "Alex's Profile"). */
  settingsTabLabel?: string;
  /** Preview tab label (e.g. "Preview · sunrisefamilycareclinic.com"). */
  previewTabLabel?: string;
  /** Test tab label (e.g. "Test Samantha"). */
  testTabLabel?: string;
  /** Auth state — Sign Up is deferred until Onboard click (modal), not a guest tab. */
  isSignedIn?: boolean;
  /** Soft-lock Configure / Preview / Hire until onboarding generates the agent. */
  disabledTabs?: {
    configure?: boolean;
    knowledge?: boolean;
    preview?: boolean;
    live_preview?: boolean;
    report?: boolean;
    company_settings?: boolean;
    pricing?: boolean;
    signup?: boolean;
    hire?: boolean;
    test_agent?: boolean;
  };
  /**
   * SiteGlue consultant chrome:
   * Left = Trish chat only (no tab header).
   * Right = Progress · Pricing · Organization · Profile · Preview · Test · Onboard
   */
  siteGlueConsultantChrome?: boolean;
  onContactSales?: () => void;
  onBookDemo?: () => void;
  /** Hide right tabs entirely (non–SiteGlue Opportunity Report until a report exists). */
  hiddenTabs?: {
    knowledge?: boolean;
    preview?: boolean;
    live_preview?: boolean;
    report?: boolean;
    company_settings?: boolean;
    pricing?: boolean;
    signup?: boolean;
    hire?: boolean;
    test_agent?: boolean;
  };
  className?: string;
};

type TabItem<T extends string> = {
  id: T;
  label: string;
  kind?: "panel" | "action";
};

/** Pill/button tabs — used for non–SiteGlue left/right bars. */
function TabBar<T extends string>({
  tabs,
  active,
  onChange,
  align,
  disabledIds,
}: {
  tabs: TabItem<T>[];
  active: T;
  onChange: (id: T) => void;
  align?: "left" | "center";
  disabledIds?: Partial<Record<T, boolean>>;
}) {
  return (
    <div
      className={cn(
        "flex shrink-0 flex-wrap gap-2 border-b border-slate-200 bg-white px-3 py-3 sm:px-4",
        align === "center" ? "justify-center" : "justify-start",
      )}
      role="tablist"
    >
      {tabs.map((tab) => {
        const disabled = Boolean(disabledIds?.[tab.id]);
        const isAction = tab.kind === "action";
        const selected = !isAction && active === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            role={isAction ? "button" : "tab"}
            aria-selected={isAction ? undefined : selected}
            aria-disabled={disabled}
            disabled={disabled}
            onClick={() => {
              if (disabled) return;
              onChange(tab.id);
            }}
            className={cn(
              "rounded-lg px-3 py-2 text-sm font-semibold transition sm:px-4",
              disabled
                ? "cursor-not-allowed bg-slate-50 text-slate-400"
                : selected
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : isAction
                    ? "border border-slate-200 bg-white text-slate-700 hover:border-[#4f81bd]/40 hover:bg-[#4f81bd]/5 hover:text-[#4f81bd]"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-slate-900",
            )}
          >
            {tab.label}
          </button>
        );
      })}
    </div>
  );
}

/** Single-word text links with pipe separators — SiteGlue dashboard (no wrapping pills). */
function TextLinkTabBar<T extends string>({
  tabs,
  active,
  onChange,
  disabledIds,
}: {
  tabs: TabItem<T>[];
  active: T;
  onChange: (id: T) => void;
  disabledIds?: Partial<Record<T, boolean>>;
}) {
  return (
    <nav
      className="flex shrink-0 flex-wrap items-center gap-x-0 gap-y-1 border-b border-slate-200 bg-white px-3 py-2.5 sm:px-4"
      role="tablist"
      aria-label="Workspace panels"
    >
      {tabs.map((tab, index) => {
        const disabled = Boolean(disabledIds?.[tab.id]);
        const isAction = tab.kind === "action";
        const selected = !isAction && active === tab.id;
        return (
          <React.Fragment key={tab.id}>
            {index > 0 ? (
              <span className="select-none px-2 text-sm text-slate-300" aria-hidden>
                |
              </span>
            ) : null}
            <button
              type="button"
              role={isAction ? "button" : "tab"}
              aria-selected={isAction ? undefined : selected}
              aria-disabled={disabled}
              disabled={disabled}
              onClick={() => {
                if (disabled) return;
                onChange(tab.id);
              }}
              className={cn(
                "whitespace-nowrap bg-transparent px-0.5 py-1 text-sm transition",
                disabled
                  ? "cursor-not-allowed text-slate-300"
                  : selected
                    ? "font-semibold text-[#4f81bd] underline decoration-[#4f81bd] decoration-2 underline-offset-4"
                    : "font-medium text-slate-600 hover:text-[#4f81bd]",
              )}
            >
              {tab.label}
            </button>
          </React.Fragment>
        );
      })}
    </nav>
  );
}

export function HireBuilderTabbedLayout({
  leftTab,
  onLeftTabChange,
  rightTab,
  onRightTabChange,
  buildPanel,
  configurePanel,
  previewPanel,
  livePreviewPanel,
  knowledgePanel,
  reportPanel,
  companySettingsPanel,
  pricingPanel,
  signupPanel,
  hirePanel,
  testAgentPanel,
  onboardTabLabel,
  settingsTabLabel,
  previewTabLabel,
  testTabLabel,
  isSignedIn: _isSignedIn = false,
  disabledTabs,
  hiddenTabs,
  siteGlueConsultantChrome = false,
  onContactSales,
  onBookDemo,
  className,
}: HireBuilderTabbedLayoutProps) {
  const hireLabel = onboardTabLabel?.trim() || "Onboard";
  const profileLabel = settingsTabLabel?.trim() || "Settings";
  const livePreviewLabel = previewTabLabel?.trim() || "Preview";
  const testLabel = testTabLabel?.trim() || "Test";
  const leftTabs: TabItem<HireBuilderLeftTab>[] = [
    { id: "build", label: "Build" },
    { id: "configure", label: "Configure" },
  ];
  // Order: Progress · Pricing · Organization · {Name}'s Profile · Preview · Test · Onboard
  // (Strategy remains in the list but is typically hidden for SiteGlue.)
  // Sign Up opens in a modal when guests click Onboard — not as a guest-only tab.
  const rightTabsAll: TabItem<HireBuilderRightTab | HireBuilderActionTab>[] =
    siteGlueConsultantChrome
      ? [
          { id: "preview", label: "Progress" },
          { id: "pricing", label: "Pricing" },
          { id: "knowledge", label: "Organization" },
          { id: "report", label: "Strategy" },
          { id: "company_settings", label: profileLabel },
          { id: "live_preview", label: livePreviewLabel },
          { id: "test_agent", label: testLabel },
          { id: "hire", label: hireLabel },
        ]
      : [
          { id: "preview", label: "Preview" },
          { id: "hire", label: hireLabel },
        ];
  const rightTabs = rightTabsAll.filter((tab) => {
    if (tab.kind === "action") return true;
    return !hiddenTabs?.[tab.id as HireBuilderRightTab];
  });
  const panelTabs = rightTabs.filter(
    (tab): tab is TabItem<HireBuilderRightTab> => tab.kind !== "action",
  );
  const activeRightTab = panelTabs.some((tab) => tab.id === rightTab)
    ? rightTab
    : (panelTabs[0]?.id ?? rightTab);

  const activePanelLabel =
    panelTabs.find((tab) => tab.id === activeRightTab)?.label ?? "Progress";

  const [mobilePanelOpen, setMobilePanelOpen] = React.useState(false);
  const prevRightTabRef = React.useRef(rightTab);

  /** On mobile, open the panel sheet when Trish routes to Profile / Strategy / etc. */
  React.useEffect(() => {
    if (!siteGlueConsultantChrome) return;
    if (typeof window === "undefined") return;
    if (window.matchMedia("(min-width: 1024px)").matches) return;
    if (prevRightTabRef.current !== rightTab && rightTab !== "preview") {
      setMobilePanelOpen(true);
    }
    prevRightTabRef.current = rightTab;
  }, [rightTab, siteGlueConsultantChrome]);

  React.useEffect(() => {
    if (activeRightTab !== rightTab) {
      onRightTabChange(activeRightTab);
    }
  }, [activeRightTab, rightTab, onRightTabChange]);

  // SiteGlue: keep left on Build chat.
  React.useEffect(() => {
    if (siteGlueConsultantChrome && leftTab !== "build") {
      onLeftTabChange("build");
    }
  }, [siteGlueConsultantChrome, leftTab, onLeftTabChange]);

  const leftDisabled = {
    build: false,
    configure: Boolean(disabledTabs?.configure),
  } as const;
  const rightDisabled: Partial<Record<HireBuilderRightTab | HireBuilderActionTab, boolean>> = {
    knowledge: Boolean(disabledTabs?.knowledge),
    preview: Boolean(disabledTabs?.preview),
    live_preview: Boolean(disabledTabs?.live_preview),
    report: Boolean(disabledTabs?.report),
    company_settings: Boolean(disabledTabs?.company_settings),
    pricing: Boolean(disabledTabs?.pricing),
    signup: Boolean(disabledTabs?.signup),
    hire: Boolean(disabledTabs?.hire),
    test_agent: Boolean(disabledTabs?.test_agent),
  };

  const handleRightTabChange = (id: HireBuilderRightTab | HireBuilderActionTab) => {
    if (id === "contact_sales") {
      onContactSales?.();
      return;
    }
    if (id === "book_demo") {
      onBookDemo?.();
      return;
    }
    onRightTabChange(id);
  };

  const RightTabBar = siteGlueConsultantChrome ? TextLinkTabBar : TabBar;

  const resolvedPanelForTab = (tab: HireBuilderRightTab): React.ReactNode => {
    switch (tab) {
      case "knowledge":
        return knowledgePanel;
      case "preview":
        return previewPanel;
      case "live_preview":
        return livePreviewPanel ?? previewPanel;
      case "report":
        return reportPanel;
      case "company_settings":
        return companySettingsPanel ?? configurePanel;
      case "pricing":
        return pricingPanel;
      case "hire":
        return hirePanel;
      case "signup":
        return signupPanel ?? hirePanel;
      case "test_agent":
        return testAgentPanel ?? hirePanel;
      default:
        return hirePanel;
    }
  };

  const mobileRightPanel = resolvedPanelForTab(activeRightTab);

  const renderRightPanels = () => (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      {siteGlueConsultantChrome ? (
        <div
          className={cn(
            "relative min-h-0 flex-1",
            "overflow-hidden",
            activeRightTab !== "knowledge" && "pointer-events-none invisible absolute inset-0",
          )}
          aria-hidden={activeRightTab !== "knowledge"}
        >
          {resolvedPanelForTab("knowledge")}
        </div>
      ) : null}
      <div
        className={cn(
          "relative min-h-0 flex-1",
          activeRightTab !== "preview" && "pointer-events-none invisible absolute inset-0",
        )}
        aria-hidden={activeRightTab !== "preview"}
      >
        {resolvedPanelForTab("preview")}
      </div>
      {siteGlueConsultantChrome ? (
        <>
          <div
            className={cn(
              "relative min-h-0 flex-1 overflow-y-auto",
              activeRightTab !== "report" && "pointer-events-none invisible absolute inset-0",
            )}
            aria-hidden={activeRightTab !== "report"}
          >
            {resolvedPanelForTab("report")}
          </div>
          <div
            className={cn(
              "relative min-h-0 flex-1 overflow-y-auto",
              activeRightTab !== "pricing" && "pointer-events-none invisible absolute inset-0",
            )}
            aria-hidden={activeRightTab !== "pricing"}
          >
            {resolvedPanelForTab("pricing")}
          </div>
          <div
            className={cn(
              "relative min-h-0 flex-1 overflow-y-auto",
              activeRightTab !== "company_settings" &&
                "pointer-events-none invisible absolute inset-0",
            )}
            aria-hidden={activeRightTab !== "company_settings"}
          >
            {resolvedPanelForTab("company_settings")}
          </div>
          <div
            className={cn(
              "relative min-h-0 flex-1 overflow-y-auto",
              activeRightTab !== "hire" && "pointer-events-none invisible absolute inset-0",
            )}
            aria-hidden={activeRightTab !== "hire"}
          >
            {resolvedPanelForTab("hire")}
          </div>
          <div
            className={cn(
              "relative min-h-0 flex-1 overflow-hidden",
              activeRightTab !== "live_preview" &&
                "pointer-events-none invisible absolute inset-0",
            )}
            aria-hidden={activeRightTab !== "live_preview"}
          >
            {resolvedPanelForTab("live_preview")}
          </div>
          <div
            className={cn(
              "relative min-h-0 flex-1 overflow-hidden",
              activeRightTab !== "test_agent" &&
                "pointer-events-none invisible absolute inset-0",
            )}
            aria-hidden={activeRightTab !== "test_agent"}
          >
            {resolvedPanelForTab("test_agent")}
          </div>
        </>
      ) : null}
      {!siteGlueConsultantChrome ? (
        <div
          className={cn(
            "relative min-h-0 flex-1 overflow-y-auto",
            activeRightTab !== "hire" && "pointer-events-none invisible absolute inset-0",
          )}
          aria-hidden={activeRightTab !== "hire"}
        >
          {hirePanel}
        </div>
      ) : null}
    </div>
  );

  const [desktopSplit] = React.useState<[number, number]>(() => {
    if (typeof window === "undefined") {
      return [DEFAULT_LEFT_PANEL_PERCENT, DEFAULT_RIGHT_PANEL_PERCENT];
    }
    try {
      const raw = window.localStorage.getItem(HIRE_BUILDER_SPLIT_STORAGE_KEY);
      if (!raw) return [DEFAULT_LEFT_PANEL_PERCENT, DEFAULT_RIGHT_PANEL_PERCENT];
      const parsed = JSON.parse(raw) as number[];
      if (
        Array.isArray(parsed) &&
        parsed.length >= 2 &&
        typeof parsed[0] === "number" &&
        typeof parsed[1] === "number" &&
        parsed[0]! > 0 &&
        parsed[1]! > 0
      ) {
        return [parsed[0]!, parsed[1]!];
      }
    } catch {
      // ignore invalid stored layout
    }
    return [DEFAULT_LEFT_PANEL_PERCENT, DEFAULT_RIGHT_PANEL_PERCENT];
  });

  return (
    <div className={cn("flex min-h-0 w-full min-w-0 flex-1 flex-col", className)}>
      {/*
        Visibility must live on a wrapper — react-resizable-panels sets inline
        display:flex on PanelGroup, which would defeat Tailwind `hidden`.
      */}
      <div className="hidden min-h-0 w-full min-w-0 flex-1 lg:flex">
        <ResizablePanelGroup
          direction="horizontal"
          autoSaveId={HIRE_BUILDER_SPLIT_STORAGE_KEY}
          className="min-h-0 w-full flex-1"
          onLayout={(sizes) => {
            if (sizes.length < 2) return;
            try {
              window.localStorage.setItem(
                HIRE_BUILDER_SPLIT_STORAGE_KEY,
                JSON.stringify(sizes),
              );
            } catch {
              // ignore quota / private mode
            }
          }}
        >
          <ResizablePanel
            defaultSize={desktopSplit[0]}
            minSize={28}
            maxSize={55}
            className="min-h-0 min-w-0"
          >
            <div className="flex h-full min-h-0 min-w-0 flex-col border-r border-slate-200">
              {siteGlueConsultantChrome ? null : (
                <TabBar
                  tabs={leftTabs}
                  active={leftTab}
                  onChange={onLeftTabChange}
                  align="center"
                  disabledIds={leftDisabled}
                />
              )}
              <div className="relative min-h-0 flex-1">
                <div
                  className={cn(
                    "absolute inset-0 flex min-h-0 flex-col overflow-hidden p-4 sm:p-5",
                    !siteGlueConsultantChrome &&
                      leftTab !== "build" &&
                      "pointer-events-none invisible",
                  )}
                  aria-hidden={!siteGlueConsultantChrome && leftTab !== "build"}
                >
                  {buildPanel}
                </div>
                {siteGlueConsultantChrome ? null : (
                  <div
                    className={cn(
                      "absolute inset-0 overflow-y-auto p-4 sm:p-5",
                      leftTab !== "configure" && "pointer-events-none invisible",
                    )}
                    aria-hidden={leftTab !== "configure"}
                  >
                    {configurePanel}
                  </div>
                )}
              </div>
            </div>
          </ResizablePanel>

          <ResizableHandle withHandle className="bg-slate-200" />

          <ResizablePanel
            defaultSize={desktopSplit[1]}
            minSize={45}
            maxSize={72}
            className="min-h-0 min-w-0"
          >
            <div className="flex h-full min-h-0 min-w-0 flex-col">
              <RightTabBar
                tabs={rightTabs}
                active={activeRightTab}
                onChange={handleRightTabChange}
                disabledIds={rightDisabled}
              />
              {renderRightPanels()}
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>

      <div
        className={cn(
          "flex min-h-0 flex-1 flex-col lg:hidden",
          siteGlueConsultantChrome ? "overflow-hidden" : "gap-0 overflow-y-auto",
        )}
      >
        {siteGlueConsultantChrome ? (
          <>
            <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden px-3 pt-2 pb-0 sm:px-4">
              {buildPanel}
            </div>
            <div className="flex shrink-0 items-center justify-between gap-2 border-t border-slate-200 bg-white px-3 py-2.5 pb-[max(0.625rem,env(safe-area-inset-bottom))] sm:px-4">
              <span className="text-xs text-slate-500">Onboarding workspace</span>
              <button
                type="button"
                onClick={() => setMobilePanelOpen(true)}
                className="inline-flex min-h-10 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-[#4f81bd] shadow-sm"
              >
                <LayoutPanelLeft className="h-4 w-4 shrink-0" aria-hidden />
                {activePanelLabel}
              </button>
            </div>
            <Sheet open={mobilePanelOpen} onOpenChange={setMobilePanelOpen}>
              <SheetContent side="bottom" className="flex h-[88dvh] flex-col gap-0 p-0">
                <SheetHeader className="shrink-0 border-b border-slate-200 px-4 py-3 text-left">
                  <SheetTitle className="text-base font-semibold text-slate-900">
                    {activePanelLabel}
                  </SheetTitle>
                </SheetHeader>
                <TextLinkTabBar
                  tabs={rightTabs}
                  active={activeRightTab}
                  onChange={(id) => handleRightTabChange(id)}
                  disabledIds={rightDisabled}
                />
                <div className="min-h-0 flex-1 overflow-y-auto">{mobileRightPanel}</div>
              </SheetContent>
            </Sheet>
          </>
        ) : (
          <>
            <TabBar
              tabs={leftTabs}
              active={leftTab}
              onChange={onLeftTabChange}
              disabledIds={leftDisabled}
            />
            <div className="p-4">{leftTab === "build" ? buildPanel : configurePanel}</div>
            <RightTabBar
              tabs={rightTabs}
              active={activeRightTab}
              onChange={handleRightTabChange}
              disabledIds={rightDisabled}
            />
            <div className="min-h-[480px]">{mobileRightPanel}</div>
          </>
        )}
      </div>
    </div>
  );
}
